import { Injectable, computed, inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { AiMedicalDocumentItem, MedicalDocumentItem } from '../models/document.interface';
import { DocumentDetailsState } from '../models/medical-documents-state.interface';
import * as MedicalDocumentsActions from '../store/medical-documents.actions';
import {
  selectAiDocumentsList,
  selectAiSearchResponseMessage,
  selectCurrentAiDocument,
  selectCurrentDocument,
  selectCurrentDocumentPreview,
  selectDocumentDetailsState,
  selectDocumentsList,
  selectDocumentsPageConfig,
  selectDocumentsSearchTerm,
  selectFilteredAiDocuments,
  selectIsAiMedicalDataEnabled,
  selectIsAiSearchChecked,
  selectIsAiSearchFeatureEnabled,
  selectIsAiSearchLoading,
  selectIsCurrentDocumentPreviewLoading,
  selectIsDocumentsListLoading,
  selectUploadingDocuments,
} from '../store/medical-documents.selectors';

@Injectable({ providedIn: 'root' })
export class MedicalDocumentsFacade {
  private readonly store = inject(Store);

  readonly isAiSearchFeatureEnabled = this.store.selectSignal(selectIsAiSearchFeatureEnabled);
  readonly isAiMedicalDataEnabled = this.store.selectSignal(selectIsAiMedicalDataEnabled);
  readonly isAiSearchChecked = this.store.selectSignal(selectIsAiSearchChecked);
  readonly aiSearchResponseMessage = this.store.selectSignal(selectAiSearchResponseMessage);
  readonly isAiSearchLoading = this.store.selectSignal(selectIsAiSearchLoading);
  readonly searchTerm = this.store.selectSignal(selectDocumentsSearchTerm);
  readonly pageConfig = this.store.selectSignal(selectDocumentsPageConfig);
  readonly documents = this.store.selectSignal(selectDocumentsList);
  readonly aiDocuments = this.store.selectSignal(selectAiDocumentsList);
  readonly filteredAiDocuments = this.store.selectSignal(selectFilteredAiDocuments);
  readonly listLoading = this.store.selectSignal(selectIsDocumentsListLoading);
  readonly currentDocument = this.store.selectSignal(selectCurrentDocument);
  readonly currentAiDocument = this.store.selectSignal(selectCurrentAiDocument);
  readonly currentPreview = this.store.selectSignal(selectCurrentDocumentPreview);
  readonly isPreviewLoading = this.store.selectSignal(selectIsCurrentDocumentPreviewLoading);
  readonly uploadingDocuments = this.store.selectSignal(selectUploadingDocuments);
  readonly detailsState = this.store.selectSignal(selectDocumentDetailsState);

  readonly isPreviewOpen = computed(() => !!this.currentPreview() || this.isPreviewLoading());

  readonly searchTerm$ = this.store.select(selectDocumentsSearchTerm);

  setAiFeatureFlags(aiMedicalDataEnabled: boolean, aiContextSearchEnabled: boolean): void {
    this.store.dispatch(MedicalDocumentsActions.setAiMedicalDataEnabled({ enabled: aiMedicalDataEnabled }));
    this.store.dispatch(MedicalDocumentsActions.setAiSearchFeatureEnabled({ enabled: aiContextSearchEnabled }));
  }

  loadAiDocuments(userId: string): void {
    this.store.dispatch(MedicalDocumentsActions.getAiDocumentsList({ userId }));
  }

  loadRegularDocuments(memberId: string, page: number, pageSize: number, searchTerm: string): void {
    this.store.dispatch(MedicalDocumentsActions.getDocumentsList({ memberId, page, pageSize, searchTerm }));
  }

  clearDocuments(): void {
    this.store.dispatch(MedicalDocumentsActions.clearDocumentsList());
  }

  setAiSearchResponseMessage(message: string): void {
    this.store.dispatch(MedicalDocumentsActions.setAiSearchResponseMessage({ message }));
  }

  openPreview(document: MedicalDocumentItem): void {
    this.store.dispatch(MedicalDocumentsActions.setCurrentDocumentDetails({ currentDocument: document }));
  }

  openAiPreview(document: AiMedicalDocumentItem): void {
    this.store.dispatch(MedicalDocumentsActions.setCurrentAiDocumentDetails({ currentAiDocument: document }));
  }

  closePreview(): void {
    this.store.dispatch(MedicalDocumentsActions.clearCurrentDocument());
  }

  closeDetails(): void {
    this.store.dispatch(MedicalDocumentsActions.closeDocumentDetails());
  }

  openEditMode(): void {
    this.store.dispatch(MedicalDocumentsActions.editDocumentDetails());
  }

  setDetailsState(state: DocumentDetailsState): void {
    this.store.dispatch(MedicalDocumentsActions.setDocumentDetailsState({ state }));
  }

  updateAiDocument(updatedDocument: AiMedicalDocumentItem): void {
    this.store.dispatch(MedicalDocumentsActions.updateAiDocument({ updatedDocument }));
  }

  promoteUploadingToAiDocument(updatedDocument: AiMedicalDocumentItem): void {
    this.store.dispatch(MedicalDocumentsActions.promoteUploadingToAiDocument({ updatedDocument }));
  }

  initFirebaseEvents(): void {
    this.store.dispatch(MedicalDocumentsActions.initFirebaseEventListener());
  }
}
